import { useMemo, useState, type ReactNode } from "react";

export interface Column<Row> {
  key: string;
  header: ReactNode;
  align?: "left" | "right" | "center";
  numeric?: boolean;
  render: (row: Row) => ReactNode;
  width?: string;
  /** Make this column's header clickable to sort. Requires `sortAccessor`. */
  sortable?: boolean;
  /** Value used to sort rows for this column. Nullish values always sort last. */
  sortAccessor?: (row: Row) => string | number | null | undefined;
}

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface TableProps<Row> {
  columns: Array<Column<Row>>;
  rows: Row[];
  rowKey: (row: Row) => string;
  empty?: ReactNode;
  /** Show shimmer rows instead of data (initial load). */
  loading?: boolean;
  loadingRows?: number;
  /** Makes rows interactive (pointer + hover + keyboard). */
  onRowClick?: (row: Row) => void;
  /** Initial sort (uncontrolled). Its column must be `sortable`. */
  defaultSort?: SortState;
}

/** Nullish last; numbers numerically; strings naturally (case-insensitive, numeric-aware). */
function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number {
  const aNil = a === null || a === undefined;
  const bNil = b === null || b === undefined;
  if (aNil && bNil) return 0;
  if (aNil) return 1;
  if (bNil) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function Table<Row>({
  columns,
  rows,
  rowKey,
  empty,
  loading,
  loadingRows = 5,
  onRowClick,
  defaultSort,
}: TableProps<Row>) {
  const align = (c: Column<Row>) => c.align ?? (c.numeric ? "right" : "left");
  const [sort, setSort] = useState<SortState | null>(defaultSort ?? null);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortAccessor) return rows;
    const acc = col.sortAccessor;
    const copy = [...rows];
    copy.sort((ra, rb) => {
      const cmp = compareValues(acc(ra), acc(rb));
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort, columns]);

  const toggleSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );

  function HeaderCell({ c }: { c: Column<Row> }) {
    const sortable = !!c.sortable && !!c.sortAccessor;
    const active = sort?.key === c.key;
    const ariaSort: "ascending" | "descending" | "none" | undefined = active
      ? sort?.dir === "asc"
        ? "ascending"
        : "descending"
      : sortable
        ? "none"
        : undefined;
    return (
      <th style={{ textAlign: align(c), width: c.width }} aria-sort={ariaSort}>
        {sortable ? (
          <button
            type="button"
            className={["fd-th-sort", active && "fd-th-sort--active"].filter(Boolean).join(" ")}
            onClick={() => toggleSort(c.key)}
          >
            <span>{c.header}</span>
            <span className="fd-th-sort__icon" aria-hidden="true">
              {active ? (sort?.dir === "asc" ? "▲" : "▼") : "↕"}
            </span>
          </button>
        ) : (
          c.header
        )}
      </th>
    );
  }

  if (loading) {
    return (
      <div className="fd-table-wrap">
        <table className="fd-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <HeaderCell key={c.key} c={c} />
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: loadingRows }).map((_, i) => (
              <tr key={i} className="fd-table__skeleton-row">
                {columns.map((c) => (
                  <td key={c.key} style={{ textAlign: align(c) }}>
                    <span className="fd-skeleton" style={{ height: 12, width: c.numeric ? "40%" : "70%" }} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (sortedRows.length === 0 && empty) {
    return <div className="fd-table-empty">{empty}</div>;
  }

  return (
    <div className="fd-table-wrap">
      <table className={["fd-table", onRowClick && "fd-table--interactive"].filter(Boolean).join(" ")}>
        <thead>
          <tr>
            {columns.map((c) => (
              <HeaderCell key={c.key} c={c} />
            ))}
          </tr>
        </thead>
        <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
          {sortedRows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter") onRowClick(row);
                    }
                  : undefined
              }
            >
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: align(c) }}>
                  {c.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
